import { DiaryLine } from './DiaryLine';
import { RatingStars } from './RatingStars';
import { tagColor, tagBackgroundColor } from '@/utils/tagTheme';

const REDDIT_URL = 'https://www.reddit.com/r/notetime';
const TWITTER_URL = 'https://x.com/davidgohberg';

type ReviewSource = 'reddit' | 'app-store';

interface Testimonial {
  body: string;
  nickname: string;
  /** App Store reviews carry a star rating; reddit posts don't have one. */
  rating?: number;
  source: ReviewSource;
}

// Verbatim quotes from public App Store reviews (all storefronts, fetched
// 2026-07-18 via the App Store Connect customerReviews API for app
// id 6760713155) and public r/notetime posts (fetched 2026-07-18). Keep
// quotes exact; trim only with ellipses, never reword.
const testimonials: Testimonial[] = [
  {
    body: 'This app is simple, easy to use and perfect for logging my ideas and specific activities throughout the day. It has helped my forgetful ADHD brain…',
    nickname: 'Andreavera',
    rating: 5,
    source: 'app-store',
  },
  {
    body: '…I was looking for a simple app to log as I go. It’s for my ADHD and the idea was I wanted something to act like a chat/messaging app but more reliable.',
    nickname: 'Double-Patty',
    source: 'reddit',
  },
  {
    body: 'First off, I love this app. So simple and clean and perfect for quick notes.',
    nickname: 't-andreozzi',
    source: 'reddit',
  },
  {
    body: 'Great app for brain dumping thoughts throughout the day! Time stamping and tagging makes for easy review at the end of the day.',
    nickname: 'DWGMPB19',
    rating: 5,
    source: 'app-store',
  },
  {
    body: 'finally a notes app that stays out of my way, entries with timing is all I need',
    nickname: 'Natalykosovsky',
    rating: 5,
    source: 'app-store',
  },
  {
    body: 'I love this app… It’s the app I never thought I needed, but I did.',
    nickname: 'demokesola',
    source: 'reddit',
  },
  {
    body: 'Very well done, helps me log by timestamp lines in one note.',
    nickname: 'Ronel99',
    rating: 5,
    source: 'app-store',
  },
  {
    body: 'I always loved the tagging feature of apple notes… but it’s only for entire notes and not individual entries in a note. This will make it much easier to brain dump during the day and review at the end of the day.',
    nickname: 'Serjo25',
    rating: 5,
    source: 'app-store',
  },
  {
    body: 'Excellent app, very useful for work and my to-do list. It also helps with tracking during meetings.',
    nickname: 'Pash12234',
    rating: 5,
    source: 'app-store',
  },
  {
    body: 'Really love the app, tags and it’s simplicity.',
    nickname: 'abhinav_sidhu',
    source: 'reddit',
  },
  {
    body: 'It’s great for maintaining a trading journal!',
    nickname: 'Itay1994',
    rating: 5,
    source: 'app-store',
  },
  {
    body: 'The app is great, i use it mainly for trading remarks and reminders and it was what i was looking for.',
    nickname: 'Steve trads.',
    rating: 5,
    source: 'app-store',
  },
  {
    body: 'This is a great simple app with super responsive developers.',
    nickname: 'Jason73550',
    rating: 5,
    source: 'app-store',
  },
];

function FollowLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-foreground underline decoration-foreground/30 underline-offset-2 transition-colors hover:decoration-foreground/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      {children}
    </a>
  );
}

function SourceChip({ source }: { source: ReviewSource }) {
  return (
    <span
      className="rounded-sm font-mono text-xs font-medium"
      style={{
        color: tagColor(source, 'light'),
        backgroundColor: tagBackgroundColor(source, 'light'),
        padding: '0 0.3em',
      }}
    >
      #{source}
    </span>
  );
}

function TestimonialCard({ t }: { t: Testimonial }) {
  return (
    <figure className="mb-3 break-inside-avoid rounded-lg border border-border/60 bg-background p-5">
      <figcaption className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-mono text-xs text-foreground">{t.nickname}</span>
        {t.rating !== undefined && (
          <RatingStars
            value={t.rating}
            label={`${t.rating} out of 5 stars`}
            className="[&_svg]:h-2.5 [&_svg]:w-2.5"
          />
        )}
        <SourceChip source={t.source} />
      </figcaption>
      <blockquote className="mt-3 text-pretty text-sm leading-relaxed text-foreground/90">
        &ldquo;{t.body}&rdquo;
      </blockquote>
    </figure>
  );
}

export function Community() {
  return (
    <>
      <DiaryLine className="mt-20 sm:mt-28" time="19:13">
        <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-[1.75rem]">
          join the community
        </h2>
      </DiaryLine>

      <DiaryLine className="mt-3">
        <p className="max-w-[58ch] text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
          follow us on <FollowLink href={TWITTER_URL}>twitter</FollowLink> and{' '}
          <FollowLink href={REDDIT_URL}>reddit</FollowLink> for feature requests and feedback.
        </p>
      </DiaryLine>

      {/* Full-width masonry inside the wide canvas; headers above stay on
          the diary spine. */}
      <div className="mt-6">
        <ul role="list" className="list-none pl-0 sm:columns-2 sm:gap-3 lg:columns-3">
          {testimonials.map((t) => (
            <li key={t.nickname}>
              <TestimonialCard t={t} />
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

export default Community;
